import { type FC } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { LAWYER_ROUTES } from "@/shared/constants/paths";

import { ValueProps } from "./ValueProps";

interface IntroStepProps {
  onContinue: () => void;
  inviterName?: string;
}

export const IntroStep: FC<IntroStepProps> = ({ onContinue, inviterName }) => {
  const isInvited = !!inviterName;

  const initials = inviterName
    ? inviterName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "";

  return (
    <div className="flex flex-col gap-10">
      {isInvited && (
        <div className="flex items-center gap-3 rounded-lg border border-primary bg-blue-100 px-6 py-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
            {initials}
          </div>
          <div>
            <p className="text-sm font-medium text-primary">
              {inviterName} added you as the attorney on their estate
            </p>
            <p className="text-sm text-secondary">
              Join {inviterName?.split(" ")[0]}&apos;s estate workspace to review the record, upload
              documents, and track progress.
            </p>
          </div>
        </div>
      )}

      <div className="text-center">
        <h2>Estate intake and discovery,</h2>
        <h2 className="italic text-brand">built for attorneys</h2>
      </div>

      <ValueProps />

      <div className="flex flex-col gap-4">
        <Button variant="brand" rounded={true} className="w-full" onClick={onContinue}>
          {isInvited ? "Accept invitation" : "Create account"}{" "}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Button>

        <p className="text-center text-sm text-secondary">
          Already have an account?{" "}
          <Link to={LAWYER_ROUTES.LOGIN} className="text-brand underline">
            Sign in
          </Link>
        </p>
      </div>

      {isInvited && (
        <div className="flex items-center justify-center gap-2">
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className="h-4 w-4 fill-current text-stone-800" aria-hidden="true" />
            ))}
          </div>
          <span className="text-sm text-secondary">Trusted by 1,000+ families and attorneys</span>
        </div>
      )}
    </div>
  );
};
